import { useState, useCallback, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Toast from '../../../components/Toast/Toast'
import {
  getMasterClasses,
  createMasterClass,
  updateMasterClass,
  deleteMasterClass,
  getConfig,
  saveConfig,
  LEARNING_ICONS,
  MODALITIES,
} from '../../../services/masterClasses'
import { getCoursesList } from '../../../services/Courses'

// ─── Constants ────────────────────────────────────────────────────────────────

const accent = '#01A76A'
const accentLight = 'rgba(1,167,106,0.08)'

const inputBase = {
  borderRadius: 8,
  fontSize: 13,
  border: '1.5px solid #e0e4ea',
  padding: '9px 13px',
  width: '100%',
  outline: 'none',
  background: '#fff',
  color: '#222',
  lineHeight: 1.5,
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
}

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#555',
  display: 'block',
  marginBottom: 5,
}

const emptyMasterClass = {
  title: '',
  topic: '',
  description: '',
  learnings: [],
  date: '',
  time: '',
  timezone: '',
  durationMinutes: 60,
  modality: MODALITIES[0],
  registrationUrl: '',
  active: true,
  bannerImageUrl: '',
  courseId: '',
}

const emptyConfig = {
  heroTitle: '',
  heroSubtitle: '',
  learningsSectionTitle: '',
  scheduleSectionTitle: '',
  scheduleSubtitle: '',
  warningCopy: '',
}

// ─── React Query keys ───────────────────────────────────────────────────────
const MASTER_CLASSES_QUERY_KEY = 'admin-master-classes'
const MASTER_CLASS_CONFIG_QUERY_KEY = 'admin-master-classes-config'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Tag = ({ children, color = accent, bg = accentLight }) => (
  <span
    style={{
      display: 'inline-block',
      background: bg,
      color,
      border: `1px solid ${color}30`,
      borderRadius: 20,
      padding: '3px 11px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.03em',
    }}
  >
    {children}
  </span>
)

const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>
      {label} {required && <span style={{ color: '#e53935' }}>*</span>}
    </label>
    {children}
    {error && (
      <small style={{ color: '#e53935', fontSize: 11, marginTop: 4, display: 'block' }}>
        {error}
      </small>
    )}
  </div>
)

// ─── Config panel (copies globales) ──────────────────────────────────────────

const ConfigPanel = ({ onSaved }) => {
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm({ defaultValues: emptyConfig })
  const [open, setOpen] = useState(false)

  const { data: config } = useQuery([MASTER_CLASS_CONFIG_QUERY_KEY], getConfig)

  useEffect(() => {
    if (config) reset({ ...emptyConfig, ...config })
  }, [config, reset])

  const { mutate: saveConfigMutate } = useMutation(saveConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries([MASTER_CLASS_CONFIG_QUERY_KEY])
      Toast({ title: 'Textos globales actualizados' })
      onSaved?.()
    },
    onError: (error) => {
      console.log(error)
      Toast({ title: 'No se pudieron guardar los textos', icon: 'error' })
    },
  })

  const onSubmit = (data) => saveConfigMutate(data)

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1.5px solid #e8ecf0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '16px 22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
          ⚙️ Textos globales de la vista pública
        </span>
        <span style={{ color: accent, fontWeight: 700 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '0 22px 20px' }}>
          <Field label="Título del hero">
            <input style={inputBase} {...register('heroTitle')} />
          </Field>
          <Field label="Subtítulo del hero">
            <textarea style={{ ...inputBase, minHeight: 70, resize: 'vertical' }} {...register('heroSubtitle')} />
          </Field>
          <Field label="Título sección aprendizajes">
            <input style={inputBase} {...register('learningsSectionTitle')} />
          </Field>
          <Field label="Título sección agenda">
            <input style={inputBase} {...register('scheduleSectionTitle')} />
          </Field>
          <Field label="Subtítulo sección agenda">
            <textarea style={{ ...inputBase, minHeight: 60, resize: 'vertical' }} {...register('scheduleSubtitle')} />
          </Field>
          <Field label="Aviso importante">
            <textarea style={{ ...inputBase, minHeight: 70, resize: 'vertical' }} {...register('warningCopy')} />
          </Field>
          <button
            type="submit"
            style={{
              background: accent,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 22px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Guardar textos
          </button>
        </form>
      )}
    </div>
  )
}

// ─── Master class form panel ──────────────────────────────────────────────────

const FormPanel = ({ editing, onCancel, onCreate, onUpdate, panelHeight }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({ defaultValues: emptyMasterClass })

  const { fields, append, remove } = useFieldArray({ control, name: 'learnings' })
  const isEditing = Boolean(editing)

  const { data: coursesResult } = useQuery(['admin-master-classes-courses'], getCoursesList)
  const courses = coursesResult?.res?.courses || []

  useEffect(() => {
    reset(
      editing
        ? { ...emptyMasterClass, ...editing, courseId: editing.courseId ?? '' }
        : emptyMasterClass
    )
  }, [editing, reset])

  const handleCancel = () => {
    reset(emptyMasterClass)
    onCancel()
  }

  const onSubmit = (data) => {
    const payload = {
      ...data,
      durationMinutes: Number(data.durationMinutes) || 0,
      active: Boolean(data.active),
      learnings: (data.learnings || []).filter((l) => l.text?.trim()),
      courseId: data.courseId === '' ? null : Number(data.courseId),
    }
    if (isEditing) {
      onUpdate(editing.id, payload)
    } else {
      onCreate(payload)
      reset(emptyMasterClass)
    }
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: isEditing ? `2px solid ${accent}` : '1.5px solid #e8ecf0',
        boxShadow: isEditing ? `0 0 0 4px ${accentLight}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        height: panelHeight,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f0f2f5', flexShrink: 0 }}>
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: isEditing ? accentLight : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {isEditing ? '✏️' : '➕'}
          </div>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e', lineHeight: 1.3 }}>
              {isEditing ? 'Editar master class' : 'Nueva master class'}
            </h6>
            <span style={{ fontSize: 11, color: isEditing ? accent : '#aaa', fontWeight: 600 }}>
              {isEditing ? 'Modo edición activo' : 'Completa los campos y guarda'}
            </span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '18px 22px' }}
      >
        <Field label="Título" required error={errors.title?.message}>
          <input
            style={inputBase}
            placeholder="Ej: Crea un producto con IA"
            {...register('title', { required: 'El título es obligatorio' })}
          />
        </Field>

        <Field label="Tema" required error={errors.topic?.message}>
          <input
            style={inputBase}
            placeholder="Ej: Negocios, Programación, IA..."
            {...register('topic', { required: 'El tema es obligatorio' })}
          />
        </Field>

        <Field label="Descripción">
          <textarea
            style={{ ...inputBase, minHeight: 80, resize: 'vertical' }}
            placeholder="Describe brevemente la master class..."
            {...register('description')}
          />
        </Field>

        <Field label="Curso que la desbloquea (opcional)">
          <select style={inputBase} {...register('courseId')}>
            <option value="">Sin curso — siempre abierta</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
          <small style={{ color: '#889', fontSize: 11, marginTop: 4, display: 'block' }}>
            Si eliges un curso, esta master class queda bloqueada para el talento hasta que apruebe su examen.
          </small>
        </Field>

        <div className="d-flex gap-2">
          <div style={{ flex: 1 }}>
            <Field label="Fecha (YYYY-MM-DD)">
              <input type="date" style={inputBase} {...register('date')} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Hora (HH:mm)">
              <input type="time" style={inputBase} {...register('time')} />
            </Field>
          </div>
        </div>

        <div className="d-flex gap-2">
          <div style={{ flex: 1 }}>
            <Field label="Zona horaria">
              <input style={inputBase} placeholder="España (CET)" {...register('timezone')} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Duración (min)">
              <input type="number" min="0" style={inputBase} {...register('durationMinutes')} />
            </Field>
          </div>
        </div>

        <Field label="Modalidad">
          <select style={inputBase} {...register('modality')}>
            {MODALITIES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>

        <Field label="URL de registro">
          <input style={inputBase} placeholder="https://luma.com/..." {...register('registrationUrl')} />
        </Field>

        <Field label="URL del banner (opcional)">
          <input style={inputBase} placeholder="https://..." {...register('bannerImageUrl')} />
        </Field>

        {/* Learnings */}
        <div style={{ marginBottom: 14 }}>
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 6 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Aprendizajes</label>
            <button
              type="button"
              onClick={() => append({ icon: LEARNING_ICONS[0], text: '' })}
              style={{
                background: accentLight,
                color: accent,
                border: `1px solid ${accent}40`,
                borderRadius: 7,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + Añadir
            </button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="d-flex gap-2 align-items-center" style={{ marginBottom: 8 }}>
              <select
                style={{ ...inputBase, width: 120, flexShrink: 0 }}
                {...register(`learnings.${index}.icon`)}
              >
                {LEARNING_ICONS.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon}
                  </option>
                ))}
              </select>
              <input
                style={inputBase}
                placeholder="Ej: Prompt Engineering"
                {...register(`learnings.${index}.text`)}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                style={{
                  background: '#fff8f8',
                  border: '1px solid #ffcdd2',
                  borderRadius: 7,
                  padding: '7px 11px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#e53935',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="d-flex align-items-center gap-2" style={{ fontSize: 13, fontWeight: 600, color: '#555', cursor: 'pointer' }}>
            <input type="checkbox" {...register('active')} />
            Master class activa (visible para talentos)
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <button
            type="submit"
            style={{
              background: accent,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
              letterSpacing: '0.01em',
            }}
          >
            {isEditing ? '✓ Actualizar master class' : '+ Agregar master class'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: '#f3f4f6',
                color: '#666',
                border: 'none',
                borderRadius: 9,
                padding: '10px 0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Cancelar edición
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ─── Master class card ────────────────────────────────────────────────────────

const MasterClassCard = ({ masterClass, isEditing, onEdit, onDelete, courseTitleById }) => (
  <div
    style={{
      borderRadius: 12,
      padding: '14px 16px',
      border: isEditing ? `1.5px solid ${accent}` : '1.5px solid #eaecf0',
      background: isEditing ? accentLight : '#fff',
      transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
      boxShadow: isEditing ? `0 0 0 3px ${accentLight}` : '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}
  >
    <div className="d-flex justify-content-between align-items-center gap-2">
      <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
        {masterClass.topic && <Tag>{masterClass.topic}</Tag>}
        {masterClass.active ? (
          <Tag color="#01A76A" bg="#CFF2DF">activa</Tag>
        ) : (
          <Tag color="#888" bg="rgba(0,0,0,0.04)">inactiva</Tag>
        )}
      </div>
      <div className="d-flex gap-1 flex-shrink-0">
        <button
          onClick={() => onEdit(masterClass)}
          style={{
            background: isEditing ? accent : '#f5f6f8',
            border: `1px solid ${isEditing ? accent : '#dde1e7'}`,
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            color: isEditing ? '#fff' : '#444',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
          }}
        >
          {isEditing ? '✓ Editando' : 'Editar'}
        </button>
        <button
          onClick={() => onDelete(masterClass.id)}
          style={{
            background: '#fff8f8',
            border: '1px solid #ffcdd2',
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            color: '#e53935',
            whiteSpace: 'nowrap',
          }}
        >
          Eliminar
        </button>
      </div>
    </div>

    <div>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
        {masterClass.title || 'Sin título'}
      </h6>
      {masterClass.description && (
        <p
          style={{
            fontSize: 12,
            color: '#556',
            margin: '6px 0 0',
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {masterClass.description}
        </p>
      )}
      <div className="d-flex flex-wrap gap-2" style={{ marginTop: 8, fontSize: 11, color: '#7a8290' }}>
        {masterClass.date && <span>📅 {masterClass.date} {masterClass.time}</span>}
        {masterClass.modality && <span>· {masterClass.modality}</span>}
        {masterClass.registrationUrl && <span>· 🔗 registro</span>}
      </div>
      {masterClass.courseId && (
        <div style={{ marginTop: 2 }}>
          <Tag color="#7048e8" bg="rgba(112,72,232,0.08)">
            🔒 Desbloquea con: {courseTitleById?.[masterClass.courseId] || `curso #${masterClass.courseId}`}
          </Tag>
        </div>
      )}
    </div>
  </div>
)

// ─── Main page ────────────────────────────────────────────────────────────────

const PANEL_HEIGHT = 'calc(100vh - 220px)'

const pageStyles = `
  .master-classes-admin-wrapper.dashboard-wrapper {
    margin-left: 24px !important;
    margin-right: 24px !important;
    min-width: 0;
  }
`

const MasterClassesPage = () => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [search, setSearch] = useState('')

  const { data: masterClasses = [] } = useQuery([MASTER_CLASSES_QUERY_KEY], getMasterClasses)
  const { data: coursesResult } = useQuery(['admin-master-classes-courses'], getCoursesList)
  const courseTitleById = (coursesResult?.res?.courses || []).reduce((acc, course) => {
    acc[course.id] = course.title
    return acc
  }, {})

  const invalidateMasterClasses = () =>
    queryClient.invalidateQueries([MASTER_CLASSES_QUERY_KEY])

  const { mutate: createMutate } = useMutation(createMasterClass, {
    onSuccess: () => {
      invalidateMasterClasses()
      Toast({ title: 'Master class creada correctamente' })
    },
    onError: (error) => {
      console.log(error)
      Toast({ title: 'No se pudo crear la master class', icon: 'error' })
    },
  })

  const { mutate: updateMutate } = useMutation(
    ({ id, data }) => updateMasterClass(id, data),
    {
      onSuccess: () => {
        setEditing(null)
        invalidateMasterClasses()
        Toast({ title: 'Master class actualizada correctamente' })
      },
      onError: (error) => {
        console.log(error)
        Toast({ title: 'No se pudo actualizar la master class', icon: 'error' })
      },
    }
  )

  const { mutate: deleteMutate } = useMutation(deleteMasterClass, {
    onSuccess: (_data, id) => {
      if (editing?.id === id) setEditing(null)
      invalidateMasterClasses()
      Toast({ title: 'Master class eliminada', icon: 'info' })
    },
    onError: (error) => {
      console.log(error)
      Toast({ title: 'No se pudo eliminar la master class', icon: 'error' })
    },
  })

  const filtered = search.trim()
    ? masterClasses.filter(
        (m) =>
          m.title?.toLowerCase().includes(search.toLowerCase()) ||
          m.topic?.toLowerCase().includes(search.toLowerCase())
      )
    : masterClasses

  const handleCreate = (data) => createMutate(data)
  const handleUpdate = (id, data) => updateMutate({ id, data })
  const handleDelete = (id) => deleteMutate(id)

  const startEdit = useCallback((masterClass) => setEditing(masterClass), [])
  const cancelEdit = useCallback(() => setEditing(null), [])

  return (
    <>
      <style>{pageStyles}</style>
      <DashboardWrapper dashboardWrapperClassName="flex-grow-1 master-classes-admin-wrapper">
        <div
          className="pt-5 mt-5"
          style={{
            background: 'linear-gradient(135deg, #01A76A 0%, #00CF83 100%)',
            borderRadius: 14,
            padding: '24px 32px',
            marginBottom: 20,
            color: '#fff',
          }}
        >
          <h4 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>Master classes</h4>
          <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>
            Gestiona las master classes y los textos globales de la vista pública.
          </p>
        </div>

        <ConfigPanel />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 20,
            alignItems: 'start',
            width: '100%',
          }}
        >
          <FormPanel
            editing={editing}
            onCancel={cancelEdit}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            panelHeight={PANEL_HEIGHT}
          />

          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              border: '1.5px solid #e8ecf0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: PANEL_HEIGHT,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '18px 22px 14px',
                borderBottom: '1px solid #f0f2f5',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                  Master classes
                </h6>
                <span
                  style={{
                    background: accentLight,
                    color: accent,
                    borderRadius: 20,
                    padding: '2px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {filtered.length}
                </span>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar master class..."
                style={{
                  borderRadius: 8,
                  border: '1.5px solid #e0e4ea',
                  padding: '7px 13px',
                  fontSize: 12,
                  outline: 'none',
                  width: 220,
                  color: '#333',
                  background: '#fafbfc',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
              {filtered.length === 0 ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    color: '#aaa',
                    paddingTop: 40,
                  }}
                >
                  <span style={{ fontSize: 44 }}>🎥</span>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay master classes creadas aún.'}
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: 14,
                    alignContent: 'start',
                  }}
                >
                  {filtered.map((masterClass) => (
                    <MasterClassCard
                      key={masterClass.id}
                      masterClass={masterClass}
                      isEditing={editing?.id === masterClass.id}
                      onEdit={startEdit}
                      onDelete={handleDelete}
                      courseTitleById={courseTitleById}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardWrapper>
    </>
  )
}

export default MasterClassesPage
